'use client';

import { Check, Copy, Mail, Share } from 'lucide-react';
import { useEffect, useState } from 'react';

import {
  FacebookIcon,
  TelegramIcon,
  WhatsAppIcon,
  XIcon,
} from '@/lib/site-social';
import {
  articleShareText,
  articleShareUrl,
  emailShareHref,
  facebookShareHref,
  isShareAbortError,
  nativeShareUrl,
  pickWebShareData,
  telegramShareHref,
  twitterShareHref,
  whatsappShareHref,
} from '@/lib/share-links';
import { cn } from '@/lib/utils';

type ArticleShareProps = {
  article: {
    title: string;
    subtitle?: string;
    slug: string;
    canonical?: string;
  };
  className?: string;
};

const buttonClass =
  'inline-flex size-10 items-center justify-center border border-white/10 text-white/55 transition-colors hover:border-white/25 hover:text-white';

export function ArticleShare({ article, className }: ArticleShareProps) {
  const url = articleShareUrl(article);
  const text = articleShareText(article);
  const payload = { url, text };

  const links = [
    {
      id: 'facebook',
      label: 'שיתוף בפייסבוק',
      href: facebookShareHref(payload),
      icon: FacebookIcon,
    },
    {
      id: 'whatsapp',
      label: 'שיתוף בוואטסאפ',
      href: whatsappShareHref(payload),
      icon: WhatsAppIcon,
    },
    {
      id: 'telegram',
      label: 'שיתוף בטלגרם',
      href: telegramShareHref(payload),
      icon: TelegramIcon,
    },
    {
      id: 'twitter',
      label: 'שיתוף ב-X',
      href: twitterShareHref(payload),
      icon: XIcon,
    },
    {
      id: 'email',
      label: 'שיתוף במייל',
      href: emailShareHref(payload),
      icon: Mail,
    },
  ] as const;

  return (
    <div className={cn('flex items-center justify-center', className)}>
      <div className="lg:hidden">
        <NativeShareButton title={text} url={url} />
      </div>
      <ul className="hidden flex-wrap items-center gap-2 lg:flex">
        {links.map(({ id, label, href, icon: Icon }) => (
          <li key={id}>
            <a
              href={href}
              target={id === 'email' ? undefined : '_blank'}
              rel="nofollow noopener noreferrer"
              aria-label={label}
              title={label}
              className={buttonClass}
            >
              <Icon className="size-4" aria-hidden />
            </a>
          </li>
        ))}
        <li>
          <CopyShareLink url={url} />
        </li>
      </ul>
    </div>
  );
}

export function NativeShareButton({
  title,
  url,
  iconOnly = false,
  className,
  tabIndex,
}: {
  title: string;
  url: string;
  iconOnly?: boolean;
  className?: string;
  tabIndex?: number;
}) {
  const [copied, setCopied] = useState(false);
  const label = copied ? 'הקישור הועתק' : 'שיתוף';

  useEffect(() => {
    if (!copied) return;
    const timer = window.setTimeout(() => setCopied(false), 2000);
    return () => window.clearTimeout(timer);
  }, [copied]);

  return (
    <button
      type="button"
      tabIndex={tabIndex}
      aria-label={label}
      title={label}
      className={cn(
        'relative z-10 inline-flex h-10 touch-manipulation items-center justify-center gap-2 border border-white/10 text-sm text-white/55 transition-colors hover:border-white/25 hover:text-white',
        iconOnly ? 'w-10' : 'px-4',
        className,
      )}
      onClick={(event) => {
        event.preventDefault();
        const shareUrl = nativeShareUrl(url);
        const data = pickWebShareData(title, shareUrl, navigator.canShare?.bind(navigator));
        const share = navigator.share?.bind(navigator);

        if (share) {
          void share(data).catch((error: unknown) => {
            if (isShareAbortError(error)) return;
            window.location.href = whatsappShareHref({ url: shareUrl, text: title });
          });
          return;
        }

        if (copyTextNow(shareUrl)) {
          setCopied(true);
          return;
        }

        window.location.href = whatsappShareHref({ url: shareUrl, text: title });
      }}
    >
      {copied ? (
        <Check className="size-4 text-brand-accent" aria-hidden />
      ) : (
        <Share className="size-4" aria-hidden />
      )}
      {iconOnly ? null : label}
    </button>
  );
}

function copyTextNow(value: string): boolean {
  const input = document.createElement('textarea');
  input.value = value;
  input.setAttribute('readonly', '');
  input.style.position = 'fixed';
  input.style.top = '0';
  input.style.left = '-9999px';
  document.body.appendChild(input);
  input.focus();
  input.select();
  input.setSelectionRange(0, value.length);
  let copied = false;
  try {
    copied = document.execCommand('copy');
  } catch {
    copied = false;
  }
  document.body.removeChild(input);
  return copied;
}

function CopyShareLink({ url }: { url: string }) {
  const [copied, setCopied] = useState(false);
  const label = copied ? 'הקישור הועתק' : 'העתקת קישור';

  useEffect(() => {
    if (!copied) return;
    const timer = window.setTimeout(() => setCopied(false), 2000);
    return () => window.clearTimeout(timer);
  }, [copied]);

  return (
    <button
      type="button"
      aria-label={label}
      title={label}
      className={buttonClass}
      onClick={async () => {
        try {
          await navigator.clipboard.writeText(url);
          setCopied(true);
        } catch {
          setCopied(false);
        }
      }}
    >
      {copied ? (
        <Check className="size-4 text-brand-accent" aria-hidden />
      ) : (
        <Copy className="size-4" aria-hidden />
      )}
    </button>
  );
}
